import {observer} from 'mobx-react-lite';
import {Form, Formik, FormikErrors, FormikHelpers, FormikProps} from 'formik';
import {useService} from '../../../../infrastructure/service/service';
import React, {useEffect, useState} from 'react';
import {DropdownOption} from '../../../../entity/core/dropdown-option';
import {User} from '../../../../entity/model/user';
import {ManageMode} from '../../../../entity/enums/manage-mode';
import {SectionTemplateDialogProps} from '../../../../entity/props/section-template/section-template-dialog-props';
import {TitleSectionTemplateDto} from '../../../../entity/dto/title-section-template-dto';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  List,
  ListItem,
  ListItemText,
  TextField,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid2';
import Box from '@mui/material/Box';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight';
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft';

import '../../../../assets/css/style.css';
import '../../../../assets/css/section-template/section-template-dialog.css';

const SectionTemplateDialog = (props: SectionTemplateDialogProps): JSX.Element => {
  const {userService, titleSectionTemplateService, sectionService} = useService();

  const [currentUser, setCurrentUser] = useState<User>();
  const [sectionOptions, setSectionOptions] = useState<DropdownOption[]>([]);
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    const init = async (): Promise<void> => {
      const user = await userService.getCurrentUser();
      setCurrentUser(user);

      setSectionOptions(sectionService.getSectionOptions([]));
    }

    init();
  }, []);

  const handleValidate = (values: TitleSectionTemplateDto): Record<string, string> => {
    const errors: Record<string, string> = {};

    if (!values.name)
      errors.name = 'Name is required.';

    if (values?.sections.length === 0)
      errors.sections = 'At least one Section is required.';

    return errors;
  }

  const handleSubmit = async (values: TitleSectionTemplateDto, {setSubmitting}: FormikHelpers<TitleSectionTemplateDto>): Promise<void> => {
    setSubmitting(true);

    const templatePayload: TitleSectionTemplateDto = {
      name: values.name,
      description: values.name,
      createdByUserID: currentUser?.id ?? '',
      sections: values.sections?.map((section) => ({name: section}))
    };

    if (props.mode === ManageMode.CREATE)
      await titleSectionTemplateService.createTitleSectionTemplate(currentUser?.organizationID ?? '', templatePayload);
    else
      await titleSectionTemplateService.updateTitleSectionTemplate(currentUser?.organizationID ?? '', props.titleSectionTemplate?.id ?? '',  templatePayload);

    props.setOpen(false);
  }

  const handleAddBtn = (setFieldValue: any, values: TitleSectionTemplateDto): void => {
    const first = sectionOptions.find((option: DropdownOption): boolean => !values.sections.includes(option.value) && option.text.toLowerCase().includes(search.toLowerCase()));
    if (first)
      setFieldValue('sections', [...values.sections, first.value]);
  }

  const isAddBtnDisabled = (values: TitleSectionTemplateDto): boolean => {
    return !sectionOptions.some((option: DropdownOption): boolean => !values.sections.includes(option.value) && option.text.toLowerCase().includes(search.toLowerCase()));
  }

  const handleRemoveBtn = (setFieldValue: any, values: TitleSectionTemplateDto): void => {
    if (values.sections.length > 0)
      setFieldValue('sections', values.sections.slice(1));
  }

  const isRemoveBtnDisabled = (values: TitleSectionTemplateDto): boolean => {
    return values.sections.length === 0;
  }

  const handleAddAllBtn = (setFieldValue: any, values: TitleSectionTemplateDto): void => {
    const toAdd = sectionOptions
      .filter((option: DropdownOption): boolean => !values.sections.includes(option.value) && option.text.toLowerCase().includes(search.toLowerCase()))
      .map((option: DropdownOption): string => option.value);

    setFieldValue('sections', [...values.sections, ...toAdd]);
  }

  return (
    <Dialog open={props.open}>
      <DialogTitle className={'zitles-dialog-title'}>
        <span>{props.mode?.toString() + 'Template'}</span>
        <IconButton id={'btn-section-template-dialog-close'} onClick={(): void => props.setOpen(false)}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Formik
          initialValues={titleSectionTemplateService.getTitleSectionTemplateDto(props.mode, props.titleSectionTemplate)}
          validate={handleValidate}
          onSubmit={handleSubmit}>
          {({values, errors, touched, handleChange, handleBlur, handleSubmit, isSubmitting, setFieldValue}: FormikProps<TitleSectionTemplateDto>): JSX.Element => (
            <Form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid size={12}>
                  <TextField
                    id={'input-section-template-name'}
                    required
                    fullWidth
                    label='Name'
                    name='name'
                    value={values.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    error={touched.name && Boolean(errors.name)}
                    helperText={touched.name && errors.name}/>
                </Grid>
                <Grid size={12}>
                  <FormControl fullWidth error={touched.sections && Boolean(errors.sections)}>
                    <TextField
                      id={'input-section-template-sections'}
                      fullWidth
                      label='Search Sections'
                      value={search}
                      onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void => setSearch(event.target.value)}/>
                    <Box display='flex' gap={2} mt={1} onBlur={handleBlur}>
                      <Box flex={1}>
                        <Typography className={'section-header'}>Available Sections</Typography>
                        <List dense className={'section-list'}>
                          {
                            sectionOptions
                              .filter((option: DropdownOption): boolean => !values.sections.includes(option.value) && option.text.toLowerCase().includes(search.toLowerCase()))
                              .map((option: DropdownOption, index: number): JSX.Element =>
                                <ListItem
                                  id={`section-template-available-section-${index}`}
                                  key={option.key}
                                  component={'div'}
                                  onClick={(): Promise<void | FormikErrors<TitleSectionTemplateDto>> => setFieldValue('sections', [...values.sections, option.value])}>
                                  <ListItemText primary={option.text} />
                                  <Typography variant='body2' fontWeight='bold' ml={2} className={'section-btn add'}>+</Typography>
                                </ListItem>
                              )
                          }
                        </List>
                      </Box>
                      <Box display='flex' flexDirection='column' justifyContent='center' alignItems='center' gap={1} mt={5}>
                        <IconButton
                          id={'btn-add-section'}
                          size='small'
                          className={`section-control ${isAddBtnDisabled(values) ? 'disabled' : ''}`}
                          onClick={(): void => handleAddBtn(setFieldValue, values)}
                          disabled={isAddBtnDisabled(values)}>
                          <ChevronRightIcon />
                        </IconButton>
                        <IconButton
                          id={'btn-remove-section'}
                          size='small'
                          className={`section-control ${isRemoveBtnDisabled(values) ? 'disabled' : ''}`}
                          onClick={(): void => handleRemoveBtn(setFieldValue, values)}
                          disabled={isRemoveBtnDisabled(values)}>
                          <ChevronLeftIcon />
                        </IconButton>
                        <IconButton
                          id={'btn-add-all-section'}
                          size='small'
                          className={`section-control ${isAddBtnDisabled(values) ? 'disabled' : ''}`}
                          onClick={(): void => handleAddAllBtn(setFieldValue, values)}
                          disabled={isAddBtnDisabled(values)}>
                          <KeyboardDoubleArrowRightIcon />
                        </IconButton>
                        <IconButton
                          id={'btn-remove-all-section'}
                          size='small'
                          className={`section-control ${isRemoveBtnDisabled(values) ? 'disabled' : ''}`}
                          onClick={(): Promise<void | FormikErrors<TitleSectionTemplateDto>> => setFieldValue('sections', [])}
                          disabled={isRemoveBtnDisabled(values)}>
                          <KeyboardDoubleArrowLeftIcon />
                        </IconButton>
                      </Box>
                      <Box flex={1}>
                        <Typography className={'section-header'}>Template Sections</Typography>
                        <List dense className={'section-list'}>
                          {
                            values.sections.map((value: string, index: number): JSX.Element => {
                              const option = sectionOptions.find((option: DropdownOption): boolean => option.value === value);
                              return (
                                <ListItem
                                  id={`section-template-selected-section-${index}`}
                                  key={value}
                                  component={'div'}
                                  onClick={(): Promise<void | FormikErrors<TitleSectionTemplateDto>> => setFieldValue('sections', values.sections.filter((section: string): boolean => section !== value))}>
                                  <ListItemText primary={option?.text ?? value} />
                                  <Typography variant='body2' fontWeight='bold' ml={2} className={'section-btn remove'}>x</Typography>
                                </ListItem>
                              );
                            })
                          }
                        </List>
                      </Box>
                    </Box>
                    <FormHelperText>
                      {touched.sections && typeof errors.sections === 'string' && errors.sections}
                    </FormHelperText>
                  </FormControl>
                </Grid>
              </Grid>
              <Divider sx={{ my: 2, mx: -3 }} />
              <Box className={'action-btn-container'}>
                <Button
                  id={'btn-section-template-cancel'}
                  disableTouchRipple
                  onClick={(): void => props.setOpen(false)}
                  className={'zitles-button cancel'}>
                                    Cancel
                </Button>
                <Button
                  id={'btn-section-template-submit'}
                  type='submit'
                  disabled={isSubmitting}
                  className={`zitles-button submit ${isSubmitting ? 'disable' : ''}`}
                  startIcon={isSubmitting ? <CircularProgress size={14} color='inherit'/> : null}>
                  {
                    isSubmitting ? `${props.mode === ManageMode.CREATE ? 'Creating' : 'Updating'}` : `${props.mode === ManageMode.CREATE ? 'Create Template' : 'Update Template'}`
                  }
                </Button>
              </Box>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  );
}

export default observer(SectionTemplateDialog);
